import { SortDirection } from '@components/Table/model/table';
import { DOCUMENT_TIME_ZONE } from '@shared/constants/documentTimeZone';
import { AxiosResponse } from 'axios';
import { toDateOnly } from '@shared/utils/dateOnly';

/**
 * @description a function get value by nested key
 */

export const getValueByNestedKey = (data: any, key: any): any => {
  const keys = key.split('.');

  let value = data;

  for (let i = 0; i < keys.length; i++) {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    value = value[keys[i]];
  }

  return value;
};

/**
 * Version timestamp for the document version selectors, printed in the
 * document zone and labelled with it ("September 20 at 5:59 PM UTC"), so it
 * agrees with the zone-labelled "Generated:" line on the document
 * (ZLAYT-1414, QA D-18). The zone defaults to DOCUMENT_TIME_ZONE, never the
 * browser's; pass one explicitly only for tests.
 */
export const formatDateForVersion = (
  date: Date,
  timeZone: string = DOCUMENT_TIME_ZONE
) => {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
    timeZone,
  };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

/**
 * @description a function to Stringify and normalize the date string from back-end
 * Returns date in long format: "January 28, 2026"
 */

export const dateFromString = (str: Date) => {
  const dateObject = new Date(str);

  // Use toLocaleDateString to format the date with long month
  const formattedDate = dateObject.toLocaleDateString('en-US', {
    month: 'long', // full month name (e.g., "January")
    day: 'numeric', // numeric day
    year: 'numeric', // numeric year
  });

  return formattedDate;
};

/**
 * @description a function to Stringify and normalize the date and time string from back-end
 * Returns datetime in long format: "January 28, 2026, 3:05 PM"
 */

export const dateTimeFromString = (str: Date) => {
  const dateObject = new Date(str);

  // Use toLocaleString to format the date with long month
  const formattedDateTime = dateObject.toLocaleString('en-US', {
    month: 'long', // full month name (e.g., "January")
    day: 'numeric', // numeric day
    year: 'numeric', // numeric year
    hour: 'numeric', // numeric hour
    minute: 'numeric', // numeric minute
  });

  return formattedDateTime;
};

export const getCurrentTime = () => {
  const dateObject = new Date();

  // Use toLocaleTimeString to format the time
  const formattedTime = dateObject.toLocaleTimeString('en-US', {
    hour12: false, // 24-hour format
    hour: '2-digit', // numeric hour
    minute: '2-digit', // numeric minute
    second: '2-digit', // numeric second
  });

  return formattedTime;
};

/**
 * @description a function normalize the date before sending to back-end
 */

export const backendDateFormat = (str: Date) =>
  // Same algorithm as before (local getters); dateOnly.ts owns it now.
  toDateOnly(new Date(str));

/**
 * @description a function to sort the items by date
 */

export const sortByDate = <T extends Record<string, any>>(
  data: T[],
  direction: SortDirection,
  key: keyof T
) =>
  data.sort((a, b) => {
    const dateA = new Date(a[key]);
    const dateB = new Date(b[key]);
    const difference = dateA.getTime() - dateB.getTime();
    return direction === SortDirection.Ascending ? difference : -difference;
  });

/**
 * @description a function to sort the items by name
 */

export const sortByName = <T extends Record<string, any>>(
  data: T[],
  direction: SortDirection
) =>
  data.sort((a, b) => {
    const difference = a.name.localeCompare(b.name);
    return direction === SortDirection.Ascending ? difference : -difference;
  });

/**
 * @description a function to sort the items by name
 */

export const sortByFileName = <T extends Record<string, any>>(
  data: T[],
  direction: SortDirection
) =>
  data.sort((a, b) => {
    const difference = a.filename.localeCompare(b.filename);
    return direction === SortDirection.Ascending ? difference : -difference;
  });

/**
 * @description a function to sort the items by custom key
 */

export const sortByKey = <T extends Record<string, any>>(
  data: T[],
  direction: SortDirection,
  key: keyof T
) =>
  data.sort((a, b) => {
    const difference = getValueByNestedKey(a, key).localeCompare(
      getValueByNestedKey(b, key)
    );
    return direction === SortDirection.Ascending ? difference : -difference;
  });

/**
 * @description a function to sort the items by quantity
 */

export const sortByQuantity = <T extends Record<string, any>>(
  data: T[],
  direction: SortDirection
) =>
  data.sort((a, b) => {
    const difference = a.quantity - b.quantity;
    return direction === SortDirection.Ascending ? difference : -difference;
  });

/**
 * @description a function to check password complexity
 */

export const checkPasswordComplexity = (password: string) => {
  const lengthRule = /.{8,}/; // at least 8 characters
  const numberRule = /.*[0-9].*/; // includes a number
  const lowerCaseRule = /.*[a-z].*/; // includes a lowercase letter
  const upperCaseRule = /.*[A-Z].*/; // includes an uppercase letter
  const specialCharRule = /.*\W.*/; // includes a special character

  let complexityScore = 0;

  // Check for length
  if (lengthRule.test(password)) complexityScore++;

  // Check for letters and numbers
  if (
    numberRule.test(password) &&
    (lowerCaseRule.test(password) || upperCaseRule.test(password))
  )
    complexityScore++;

  // Check for mix of upper and lower case
  if (lowerCaseRule.test(password) && upperCaseRule.test(password))
    complexityScore++;

  // Check for special characters
  if (specialCharRule.test(password)) complexityScore++;

  return complexityScore;
};

// export const range = (startingNumber: number, count: number) => {
//   return Array.apply(0, Array(count - 1)).map(
//     (_, index) => index + startingNumber
//   );
// };

/**
 * @description a utility function to return a range of numbers in array
 */

export const range = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const formatNumberWithComma = (value: string) => {
  value = value.replace(/[^0-9,]/g, '');
  value = value.replace(/[,$]/g, '');
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export function getCookie(name: string) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === `${name}=`) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} bytes`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatFile(file: File) {
  return {
    size: formatFileSize(file.size),
    filename: file.name,
  };
}

export function extractFilename(url: string) {
  const match = url.match(/\/([^\/?#]+)[^\/]*$/);
  if (match && match[1]) {
    return match[1];
  }
  return 'undefined';
}

/**
 * @description - utility that gets date in Date format and convert it to the string in format "1 Jan"
 * @param date - Date
 */

export function formatDate(date: Date) {
  const day = date.getDate();
  const monthIndex = date.getMonth();

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return `${day} ${months[monthIndex]}`;
}

/**
 * @description - utility to count how much time run to display appropriate
 * time measurement ("few sec ago" | "min ago" | "hr ago")
 * @param time - number
 */

export function formatTimeAgo(time: number) {
  if (time < 1 / 60) {
    return 'few sec ago';
  }
  if (time < 1) {
    const minutes = Math.floor(time * 60);
    return `${minutes} min ago`;
  }
  const hours = Math.floor(time);
  return `${hours} hr ago`;
}

/**
 * @description - Utility to parse a JSON string of sources and merge their results into a single list.
 * @param source - Optional JSON string representing a list of sources.
 * @returns - An array of results merged from all sources. If parsing fails, returns an empty array.
 */

export const parseSource = (source?: string) => {
  try {
    let resultSourceList: any[] = [];

    const sourceList = source ? JSON.parse(source) : [];

    sourceList.forEach(
      (item: any) => (resultSourceList = [...resultSourceList, ...item.results])
    );

    return resultSourceList;
  } catch (e) {
    console.log(e);
    return [];
  }
};

export const formatCurrency = (
  value: number,
  maximumFractionDigits: number = 4
) => {
  const moneyFormatter = Intl.NumberFormat('en-US', {
    currency: 'USD',
    currencyDisplay: 'symbol',
    currencySign: 'standard',
    style: 'currency',
    minimumFractionDigits: 2,
    maximumFractionDigits,
    minimumIntegerDigits: 1,
  });

  return moneyFormatter.format(value);
};

export const getDiscountMeta = (
  listPrice?: string | number | null,
  price?: string | number | null
) => {
  const lp = listPrice != null ? Number(listPrice) : NaN;
  const p = price != null ? Number(price) : NaN;
  const valid = Number.isFinite(lp) && Number.isFinite(p) && lp > p && lp > 0;
  if (!valid) {
    return {
      hasDiscount: false,
      percentOff: 0,
      youSave: '',
      listPriceFormatted: '',
      priceFormatted: Number.isFinite(p)
        ? formatSubscriptionPriceCurrency(p)
        : '',
    };
  }
  const savings = lp - p;
  const percentOff = Math.round((savings / lp) * 100);
  return {
    hasDiscount: true,
    percentOff,
    youSave: formatSubscriptionPriceCurrency(savings),
    listPriceFormatted: formatSubscriptionPriceCurrency(lp),
    priceFormatted: formatSubscriptionPriceCurrency(p),
  };
};

export const formatSubscriptionPriceCurrency = (
  value: number,
  maximumFractionDigits: number = 2
) => {
  const hasDecimals = value % 1 !== 0;

  const moneyFormatter = Intl.NumberFormat('en-US', {
    currency: 'USD',
    currencyDisplay: 'symbol',
    currencySign: 'standard',
    style: 'currency',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits,
    minimumIntegerDigits: 1,
  });

  return moneyFormatter.format(value);
};

export const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const convertFileToBlobAndDownload = async (
  fileUrl: string,
  fileName: string
) => {
  const blob = await fetch(fileUrl).then((r) => r.blob());
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export function appendObjectToFormData(
  formData: FormData,
  data: Record<string, any>
): void {
  Object.entries(data).forEach(([key, value]) => {
    if (value == null) return;

    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
}

export const saveBlobToDisk = (response: AxiosResponse, fallback: string) => {
  const { data, headers } = response;

  const blob = new Blob([data], {
    type: headers['content-type'] || 'application/octet-stream',
  });

  const filename =
    headers['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1] ||
    fallback;

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(2)} ${units[i]}`;
};

/**
 * @description Get user initials from name consistently
 * @param name - Full name of the user
 * @returns Initials (first letter of first name + first letter of last name, or first letter only if single name)
 */
export const getUserInitials = (name: string): string => {
  if (!name || typeof name !== 'string') return '';

  const trimmedName = name.trim();
  if (!trimmedName) return '';

  const parts = trimmedName.split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0][0].toUpperCase();

  // Return first letter of first name + first letter of last name
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
